/**
 * The commentary row beneath the table (feature 023, FR-031).
 *
 * Originally Jeff's commentary and nothing else: it rendered only when the hand actually carried
 * a comment — no placeholder before the answer, and no "THE ANSWER" fallback for hands with no
 * commentary (client decision, 2026-07-21). Most tournament hands have no comment, so the old
 * always-present version showed an empty reserved box on most questions and a restated correct
 * answer on the rest; the correct answer now lives in the decision panel next to the verdict,
 * where the learner is already looking.
 *
 * Feature 027 gave it a second voice. Hands downloaded from online sources never carried Jeff's
 * commentary, so on those the box was simply absent and the learner got no explanation at all.
 * Where the AI engine predicted the very call Jeff made, its reasoning now fills that void under
 * a "Hawkes AI says" label — and where it did not, the panel says so rather than staying silent,
 * so a learner on a downloaded hand always knows whether an explanation exists (client decision,
 * 2026-08-19). Jeff's own words still win wherever a hand has them.
 *
 * Callers render the Jeff variant conditionally — `hasExpertComment` is exported so the test for
 * "should it appear" lives in one place rather than being re-derived per caller. The AI variant
 * is unconditional on a downloaded hand: `aiCommentText` turns a reason (or its absence) into the
 * line to show.
 *
 * It stays a live region so a screen reader announces the commentary when it arrives.
 */

import { renderWithEditorNote } from '../../utils/editorNote';

/** Shown in place of an AI comment when none is available for the answered call. */
export const AI_COMMENT_UNAVAILABLE = 'Information not available';

/**
 * Prefix on every AI explanation — it names whose reasoning this is and why it is being shown.
 * Carries no trailing space: the gap between it and the reason is rendered, so that bolding the
 * prefix cannot bold a trailing space along with it.
 */
export const AI_COMMENT_PREFIX = 'AI predicted as Jeff because';

/**
 * The AI line for one answered call, split into the parts the panel styles differently: the
 * prefix (rendered bold, so the attribution reads at a glance and is not mistaken for part of
 * the engine's own words) and the reasoning behind it. `prefix` is null for the unavailable
 * notice, which is a statement in its own right rather than a preface to anything.
 */
export function aiCommentParts(reason?: string | null): { prefix: string | null; text: string } {
  const trimmed = reason?.trim();
  return trimmed
    ? { prefix: AI_COMMENT_PREFIX, text: trimmed }
    : { prefix: null, text: AI_COMMENT_UNAVAILABLE };
}

/**
 * The same line as flat text — what a screen reader or a test reads off the panel. Derived from
 * `aiCommentParts` rather than composed separately, so the two can never drift.
 */
export function aiCommentText(reason?: string | null): string {
  const { prefix, text } = aiCommentParts(reason);
  return prefix ? `${prefix} ${text}` : text;
}

/**
 * Which commentary panel an answered question should show, if any (feature 027).
 *
 * One rule for every surface, because the answer differs only in its inputs: Jeff's own words
 * win wherever a hand has them; the AI panel applies to downloaded tournament hands and to
 * nothing else — a local problem carries curated commentary of its own, which is the void this
 * feature exists to fill, so those keep the original "no comment, no panel" behaviour.
 *
 * `'none'` before an answer is submitted: commentary is answer-feedback, and showing an empty
 * or unavailable box next to an unanswered question tells the learner nothing.
 */
export function explainVariantFor(options: {
  /** Has the learner answered? Commentary is feedback, so nothing shows before that. */
  answered: boolean;
  /** Jeff's commentary for the hand, when it has any. */
  expertComment?: string | null;
  /** Whether the answered question came from the downloaded tournament pool. */
  isTournamentHand: boolean;
}): 'jeff' | 'ai' | 'none' {
  if (!options.answered) return 'none';
  if (hasExpertComment(options.expertComment)) return 'jeff';
  return options.isTournamentHand ? 'ai' : 'none';
}

/**
 * One AI line: the attribution in bold, then the engine's words. Split into an element rather
 * than a string so the prefix can carry weight — it is the part that says these are the AI's
 * words and not Jeff's, which is the distinction the whole panel turns on.
 *
 * A single space separates them, written as its own node so JSX cannot trim it away.
 */
function AiComment({ reason }: { reason?: string | null }) {
  const { prefix, text } = aiCommentParts(reason);

  if (!prefix) return <>{text}</>;

  return (
    <>
      <strong className="font-bold" data-testid="explain-comment-prefix">
        {prefix}
      </strong>{' '}
      {text}
    </>
  );
}

/**
 * The commentary row's own chrome, split out as constants (feature 029) so a surface whose
 * full-width row is NOT commentary can wear the identical box without copying its class strings.
 *
 * The conceptual-problems page is that surface: its row is the problem's descriptive ANSWER, which
 * is the same shape of content — a paragraph of prose under the three panels — and therefore wants
 * the same brass-ruled notice box and the same per-band height caps. `explain-panel` and
 * `explain-comment-scroll` are the CSS hooks index.css sizes those by, and `.explain-comment-scroll`
 * is one of the two scrollers the no-scrollbar contract allows, so a row that wants either must
 * carry the real class name rather than a look-alike.
 */
export const EXPLAIN_PANEL_CLASS =
  'explain-panel flex items-start gap-4 rounded-[10px] border-l-[6px] border-brass bg-notice px-4 py-3 text-notice-ink sm:px-[22px] sm:py-[18px]';

/** The pill that names whose words these are. Sentence case in the source, capitalised by CSS. */
export const EXPLAIN_TAG_CLASS =
  'shrink-0 whitespace-nowrap rounded-full bg-primary-600 px-2.5 py-1 text-xs font-extrabold uppercase tracking-[0.1em] text-white narrow:normal-case narrow:tracking-normal';

/** The capped, scrollable text column. Carries the 3-line floor the height-starved bands hold it to. */
export const EXPLAIN_SCROLL_CLASS =
  'explain-comment-scroll min-w-0 text-[14.5px] leading-[1.55] narrow:max-h-36 narrow:overflow-y-auto scrollable';

export interface ExplainPanelProps {
  /** The commentary to show. The Jeff variant renders nothing when this is absent or blank. */
  expertComment?: string | null;
  /**
   * Whose commentary this is. `jeff` (the default) keeps the pre-027 behaviour exactly — including
   * hiding itself on an empty comment. `ai` labels the box "Hawkes AI says" and always renders,
   * since its caller has already decided the hand is one the panel belongs on.
   */
  variant?: 'jeff' | 'ai';
}

export function hasExpertComment(comment?: string | null): boolean {
  return Boolean(comment && comment.trim());
}

export function ExplainPanel({ expertComment, variant = 'jeff' }: ExplainPanelProps) {
  const isAi = variant === 'ai';
  if (!isAi && !hasExpertComment(expertComment)) return null;

  return (
    <div
      // `explain-panel` is the CSS hook the viewport-locked bands size this box by (index.css).
      // The panel itself never shrinks — the comment inside it is capped instead, so the box is
      // always drawn whole and only its text scrolls.
      className={EXPLAIN_PANEL_CLASS}
      data-testid="explain-panel"
      data-variant={variant}
      role="status"
      aria-live="polite"
    >
      {/* Written in sentence case and capitalised by CSS above the phone breakpoint, not typed
          in capitals: the rule governs the RENDERED word, so "JEFF SAYS" in the source would
          have been caught exactly as a text-transform is (constitution VI, amended 2026-07-29 —
          which names this very label as its example). 11px -> 12px for the same amendment.
          "Hawkes AI says" is written the same way for the same reason. */}
      <span
        className={EXPLAIN_TAG_CLASS}
        data-testid="explain-tag"
      >
        {isAi ? 'Hawkes AI says' : 'Jeff says'}
      </span>
      {/* Phones cap the comment and scroll it internally (the pre-023 /problems treatment:
          a fixed-height feedback area whose long Jeff comment scrolls) so a wordy comment
          never pushes the viewport-locked table frame into page scrolling.
          `explain-comment-scroll` is the landscape-phone handle for the same cap: `narrow:`
          stops at 550px of height, and unfolded fold phones (832x623) are landscape phones
          taller than that — see the landscape block in index.css.
          Every one of those caps has a 3-line FLOOR (client request, 2026-07-30): this box and
          the auction are the only two places on the page a scrollbar is allowed, and a scroller
          showing one and a half lines is not a reading surface. */}
      <div className={EXPLAIN_SCROLL_CLASS}>
        <p data-testid="explain-comment">
          {isAi ? <AiComment reason={expertComment} /> : renderWithEditorNote(expertComment)}
        </p>
      </div>
    </div>
  );
}
